import { useEffect, useRef, useState } from "react";

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  remove: (id: string) => void;
  reset: (id: string) => void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

let scriptLoading: Promise<void> | undefined;
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptLoading) {
    scriptLoading = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      const fail = () => { clearTimeout(timer); script.remove(); scriptLoading = undefined; reject(new Error("Verification could not load. Please retry.")); };
      const timer = window.setTimeout(fail, 15000);
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.onload = () => { clearTimeout(timer); resolve(); };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return scriptLoading;
}

/** Both sign-in pages use the same runtime configuration as the API. */
export function useTurnstile(active: boolean) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const token = useRef<string | undefined>(undefined);
  const enabled = useRef(false);
  const [ready, setReady] = useState(!active);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!active) { setReady(true); return; }
    let cancelled = false;
    setReady(false);
    setError(null);
    token.current = undefined;
    const failed = () => { token.current = undefined; setReady(false); setError("Verification could not finish. Please retry."); };
    void fetch("/v1/auth/turnstile", { cache: "no-store", signal: AbortSignal.timeout(10000) }).then(async (res) => {
      if (!res.ok) throw new Error("Verification unavailable");
      return await res.json() as { enabled: boolean; siteKey?: string };
    }).then(async (config) => {
      if (cancelled) return;
      enabled.current = config.enabled;
      if (config.enabled === false) { setReady(true); return; }
      if (config.enabled !== true || !config.siteKey) throw new Error("Invalid verification configuration");
      await loadScript();
      if (cancelled || !container.current) return;
      widget.current = window.turnstile!.render(container.current, {
        sitekey: config.siteKey,
        action: "signin",
        size: container.current.clientWidth < 300 ? "compact" : "flexible",
        callback: (value: string) => { token.current = value; setError(null); setReady(true); },
        "expired-callback": () => { token.current = undefined; setReady(false); },
        "error-callback": failed,
        "timeout-callback": failed,
      });
    }).catch(() => { if (!cancelled) failed(); });
    return () => {
      cancelled = true;
      if (widget.current !== null) window.turnstile?.remove(widget.current);
      widget.current = null;
      token.current = undefined;
    };
  }, [active, attempt]);

  function getToken() {
    if (enabled.current && !token.current) throw new Error("Complete the verification and try again.");
    return token.current;
  }
  function reset() {
    token.current = undefined;
    if (enabled.current && widget.current !== null) {
      setReady(false);
      window.turnstile?.reset(widget.current);
    }
  }
  const element = active ? <div className="mt16">
    <div ref={container} />
    {error && <p className="err tiny" role="alert">{error} <button type="button" className="btn sm" onClick={() => setAttempt((n) => n + 1)}>Retry verification</button></p>}
    {!ready && !error && <p className="tiny" role="status">Complete the verification to continue.</p>}
  </div> : null;
  return { ready, getToken, reset, element };
}
